import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import L from 'leaflet';

/**
 * Loads and renders restricted areas for a jobsite.
 * Uses the existing /api/customer/restrictedAreas CRUD endpoints.
 */
export default function useRestrictedAreas({ url, jobsite, mapRef, visible = true }) {
  const [areas, setAreas] = useState([]);
  const layerGroupRef = useRef(null);

  // Feature group on the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || layerGroupRef.current) return;
    layerGroupRef.current = new L.FeatureGroup();
    map.addLayer(layerGroupRef.current);
    return () => {
      if (layerGroupRef.current) {
        try { map.removeLayer(layerGroupRef.current); } catch (_) {}
        layerGroupRef.current = null;
      }
    };
  }, [mapRef]);

  // Load areas when jobsite changes
  const load = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setAreas([]);
      return;
    }
    try {
      const resp = await axios.get(
        `${url}/api/customer/restrictedAreas?jobSiteId=${jobsite._id}`
      );
      if (resp.data?.success && Array.isArray(resp.data.restrictedAreas)) {
        setAreas(resp.data.restrictedAreas);
      } else {
        setAreas([]);
      }
    } catch (err) {
      console.error('Error loading restricted areas:', err);
      setAreas([]);
    }
  }, [url, jobsite]);

  useEffect(() => { load(); }, [load]);

  // Render restricted area polygons
  useEffect(() => {
    const map = mapRef.current;
    const group = layerGroupRef.current;
    if (!map || !group) return;
    group.clearLayers();

    if (!visible) return;

    areas.forEach((ra) => {
      if (!ra.paths?.length || ra.paths.length < 3) return;
      const coords = ra.paths.map(p => [p.lat, p.lng]);
      const layer = L.polygon(coords, {
        color: ra.strokeColor || '#FF0000',
        weight: ra.strokeWeight || 3,
        opacity: 0.9,
        fillColor: ra.fillColor || '#FF0000',
        fillOpacity: 0.25,
      });
      const tooltipHtml = `<strong>${ra.name || 'Restricted Area'}</strong>` +
        (ra.restrictionDetails?.restrictionType ? `<br/>${ra.restrictionDetails.restrictionType}` : '') +
        (ra.restrictionDetails?.contactName ? `<br/>${ra.restrictionDetails.contactName}` : '');
      layer.bindTooltip(tooltipHtml);
      group.addLayer(layer);
    });
  }, [areas, visible, mapRef]);

  // Save — either create (new paths) or update (existing _id)
  const saveArea = useCallback(async (areaData, existing) => {
    if (!url || !jobsite?._id) return null;
    try {
      if (existing?._id) {
        const resp = await axios.put(
          `${url}/api/customer/restrictedAreas/${existing._id}`,
          { ...existing, ...areaData }
        );
        if (resp.data?.success) {
          setAreas(prev => prev.map(ra =>
            ra._id === existing._id ? { ...ra, ...areaData } : ra
          ));
          return resp.data;
        }
      } else {
        const payload = {
          jobSiteId: jobsite._id,
          restrictedAreas: [{
            ...areaData,
            jobSite: jobsite._id,
            client: jobsite.client,
            isActive: true,
          }],
        };
        const resp = await axios.post(
          `${url}/api/customer/restrictedAreas`,
          payload
        );
        if (resp.data?.success) {
          await load(); // re-fetch to get the fresh record
          return resp.data;
        }
      }
    } catch (err) {
      console.error('saveArea error:', err);
    }
    return null;
  }, [url, jobsite, load]);

  const deleteArea = useCallback(async (areaId) => {
    if (!url || !areaId) return;
    try {
      await axios.delete(`${url}/api/customer/restrictedAreas/${areaId}`);
      setAreas(prev => prev.filter(ra => ra._id !== areaId));
    } catch (err) {
      console.error('deleteArea error:', err);
    }
  }, [url]);

  return { areas, saveArea, deleteArea, reload: load };
}
